import {Map} from 'immutable';
import makeConstants from 'lib/makeConstants';
import type {GuessModelV1} from 'resources/augmented/Guess/GuessModel.v1';

type Guess = GuessModelV1;

/**
 * Map that holds the result of a guess placed on a given question, and its guess resource if one is available
 */
type GuessResult = Map<string, unknown>;

/**
 * A Map whose keys are question IDs, and its values are a GuessResult
 */
type GuessResultsMap = Map<string, GuessResult>;

export const resultValues = makeConstants('correct', 'incorrect', 'indeterminate');

export function makeGuessResultManager(initialData: Record<string, unknown> | GuessResultsMap = {}) {
  const guessResultsMap = Map(initialData) as GuessResultsMap;
  return {
    addGuessResults(guessesObj: Record<string, GuessResult> | GuessResultsMap) {
      const merged = Map.isMap(guessesObj)
        ? guessResultsMap.merge(guessesObj as GuessResultsMap)
        : guessResultsMap.merge(guessesObj as Record<string, GuessResult>);
      return makeGuessResultManager(merged);
    },
    updateGuess(
      questionId: string,
      guess: Guess,
      storeGuess = false,
      partialCreditEnabled = false
    ) {
      const result = guess.getCorrectness(partialCreditEnabled);

      return makeGuessResultManager(
        guessResultsMap.set(
          questionId,
          Map({
            result,
            guess: storeGuess ? guess : null
          })
        )
      );
    },
    getGuessResult(questionId: string) {
      return guessResultsMap.has(questionId) ? guessResultsMap.getIn([questionId, 'result']) : null;
    },
    getGuess(questionId: string) {
      if (!guessResultsMap.has(questionId)) {
        return null;
      }
      const guess = guessResultsMap.getIn([questionId, 'guess']);
      if (!guess) {
        throw Error(
          `Attempted to get guess for question ${questionId}, which has a guess entry, but no guess result. Please ensure you've bootstrapped the guesses and passing true as the storeGuess param in the updateGuess method.`
        );
      }
      return guess;
    },
    getGuessResultsMap() {
      return guessResultsMap;
    }
  };
}

export function getGuessesFromAssignment(assignment: {
  getGuesses: () => Iterable<Guess> & {
    reduce: (
      callback: (acc: GuessResultsMap, guess: Guess) => GuessResultsMap,
      initialValue: GuessResultsMap
    ) => GuessResultsMap;
  };
}): GuessResultsMap {
  return assignment.getGuesses().reduce((acc, guess) => {
    const questionId = guess.getQuestion().getId();
    const result = guess.isCorrect() ? resultValues.correct : resultValues.incorrect;
    return acc.set(
      questionId,
      Map({
        result,
        guess
      })
    );
  }, Map() as GuessResultsMap);
}
